import React, { useState, useContext, useEffect } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";

// @mui icons
import GoogleIcon from "@mui/icons-material/Google";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import { UserContext } from "context";
import { GoogleLogin } from "react-google-login";
import { gapi } from "gapi-script";

// Images
import bgImage from "assets/images/Signin-backgorund.png";

// ENV Values
const apiUrl = process.env.REACT_APP_AUTH_API_URL;
const redirectUrl = process.env.REACT_APP_REDIRECT_URL;
const googleClientID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function Basic() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    title: "",
    message: "",
    color: "success",
    icon: "check",
  });

  useEffect(() => {
    function start() {
      gapi.client.init({
        clientId: `${googleClientID}`,
        scope: "profile email",
      });
    }

    gapi.load("client:auth2", start);
  }, []);

  const navigate = useNavigate();
  const { login } = useContext(UserContext);

  const closeSB = () => setSnackbar({ open: false });

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLogIn = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${apiUrl}/login.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        setSnackbar({
          open: true,
          title: "Error",
          message: data.message || "Something went wrong",
          color: "error",
          icon: "warning",
        });
        return;
      }

      setSnackbar({
        open: true,
        title: "Success",
        message: "Login successful!",
        color: "success",
        icon: "check",
      });
      localStorage.setItem("isGoogleUser", "false");
      login(data.jwt); // Set the token and user state
      navigate("/dashboard");
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
      setSnackbar({
        open: true,
        title: "Error",
        message: error.message || "There was an error submitting the form",
        color: "error",
        icon: "warning",
      });
    }
  };

  const handleGoogleSuccess = async (response) => {
    try {
      const res = await fetch(`${apiUrl}/google_login.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: response.tokenId }),
      });

      const text = await res.text();

      if (!res.ok) {
        const errorData = JSON.parse(text);
        setSnackbar({
          open: true,
          title: "Error",
          message: errorData.message || "Something went wrong",
          color: "error",
          icon: "warning",
        });
        return;
      }

      const data = JSON.parse(text);

      setSnackbar({
        open: true,
        title: "Success",
        message: "Login successful!",
        color: "success",
        icon: "check",
      });
      localStorage.setItem("isGoogleUser", "true");
      login(data.jwt); // Set the token and user state
      navigate("/dashboard");
    } catch (error) {
      console.error("Google Sign In was unsuccessful. Try again later", error);
      setSnackbar({
        open: true,
        title: "Error",
        message: error.message || "Google Sign In was unsuccessful. Try again later",
        color: "error",
        icon: "warning",
      });
    }
  };

  const handleGoogleFailure = (error) => {
    console.error("Google Sign In was unsuccessful. Try again later", error);
    setSnackbar({
      open: true,
      title: "Error",
      message: "Google Sign In was unsuccessful. Try again later",
      color: "error",
      icon: "warning",
    });
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="secondary"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Accedi
          </MDTypography>
          <Grid container spacing={3} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={2}>
              <MDTypography component={MuiLink} href="#" variant="body1" color="white">
                <GoogleLogin
                  clientId={googleClientID}
                  render={(renderProps) => (
                    <MDTypography
                      component={MuiLink}
                      variant="body1"
                      color="white"
                      onClick={renderProps.onClick}
                      disabled={renderProps.disabled}
                    >
                      <GoogleIcon color="inherit" />
                    </MDTypography>
                  )}
                  onSuccess={handleGoogleSuccess}
                  onFailure={handleGoogleFailure}
                  cookiePolicy={"single_host_origin"}
                  redirectUri={`${redirectUrl}`}
                />
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleLogIn}>
            <MDBox mb={2}>
              <MDInput
                label="Email"
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label="Password"
                fullWidth
                name="password"
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Ricorda le mie credenziali
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" color="secondary" fullWidth type="submit">
                Accedi
              </MDButton>
            </MDBox>
            <MDBox textAlign="center">
              <MDTypography variant="button" color="text">
                Non hai ancora un Account?{" "}
                <MDTypography
                  component={Link}
                  to="/app/authentication/sign-up"
                  variant="button"
                  color="secondary"
                  fontWeight="medium"
                  textGradient
                >
                  Registrati
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox textAlign="center">
              <MDTypography
                component={Link}
                to="/app/authentication/reset-password/passwordResetRequest"
                variant="button"
                color="secondary"
                fontWeight="medium"
                textGradient
              >
                Password dimenticata?
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
      <MDSnackbar
        open={snackbar.open}
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.message}
        onClose={closeSB}
        close={closeSB}
        bgWhite
      />
    </BasicLayout>
  );
}

export default Basic;

